import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle, memo } from 'react';
import { View, Text, StyleSheet, TextInput, Pressable } from '../lib/react-native-compat';
import { selectedJobAtom, jobUserDataAtom, jobCostDetailAtom, selectedJobDataAtom, selectedVersionAtom, isLiveVersionAtom, phasedForecastAtom, projectCompleteAtom, dataSourceVersionAtom, selectedVersionLockedAtom, jobPhaseCodesAtom, actualHoursAtom, budgetHoursAtom, updatedBudgetHoursAtom, eacHoursAtom } from '../store/company';
import { useAtom } from 'jotai';
import moment from 'moment';
import { updateUserDataArray, loadUserData, fNum, pNum } from '../db/company';
import { standardDisciplineFor, STANDARD_AREAS } from '../db/wbsStandard';
import { voltaGridWbsFor, voltaGridGroupFor, voltaGridDisciplineRank, foldVoltaGridDisciplines } from '../db/wbsVoltaGrid';
import { parseQtyMath, sharedUnit } from '../helpers/disciplineQty';
import { isVoltaGridJob } from '../helpers/voltaGrid';

const s = (w = null) => StyleSheet.create({
    rate: {
        width: 60,
        height: 24,
        border: '1px solid #aeadaf',
        padding: 3,
        fontSize: 10,
    },
    rateB: {
        width: 60,
        height: 24,
        border: '1px solid white',
        padding: 3,
        fontSize: 10,
    },
    span: {
        width: 113,
        padding: 3,
        height: 24,
        alignItems: 'center',
        textAlign: 'right',
        fontSize: 10,
    },
    spanS: {
        width: 90,
        padding: 3,
        height: 24,
        alignItems: 'center',
        textAlign: 'right',
        fontSize: 10,
    },
    break: {
        width: 0,
        padding: 3,
        height: 24
    },
    total: {
        width: 83,
        border: '1px solid #aeadaf',
        textAlign: 'center',
        padding: 3,
        fontSize: 10,
    },
    title: {
        width: w ? Math.round(w * 0.75) : 75,
        border: '1px solid #aeadaf',
        borderRight: '0 solid #aeadaf',
        textAlign: 'center',
        padding: 3,
        fontSize: 10,
    },
    cell: {
        width: w ? Math.round(w * 0.75) : 75,
        border: '1px solid #aeadaf',
        borderRight: '0 solid #aeadaf',
        textAlign: 'right',
        padding: 3,
        fontSize: 10,
    },
    dropdown: {
        height: 30,
        width: w ? Math.round(w * 0.75) : 75,
        paddingHorizontal: 6,
        border: '1px solid #aeadaf',
        fontSize: 10,
    },
    dropdownH: {
        height: 30,
        width: w ? Math.round(w * 0.75) : 75,
        paddingHorizontal: 6,
        border: '1px solid #aeadaf',
        backgroundColor: '#efefef',
        fontSize: 10,
    },
    icon: {
        marginRight: 4,
    },
    label: {
        position: 'absolute',
        backgroundColor: 'white',
        left: 17,
        top: 6,
        zIndex: 999,
        paddingHorizontal: 6,
        fontSize: 11,
    },
    placeholderStyle: {
        fontSize: 12,
        fontWeight: '300',

    },
    selectedTextStyle: {
        fontSize: 12,
        color: 'black'
    },
})

// Modern overview table styles
const ovStyles = {
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 10, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    thGroup: { padding: '6px 8px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', background: '#f1f5f9', borderBottom: '2px solid #e2e8f0', textAlign: 'center', whiteSpace: 'nowrap' },
    th: { padding: '5px 8px', fontSize: 9, fontWeight: 600, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'right', whiteSpace: 'nowrap' },
    thDesc: { padding: '5px 8px', fontSize: 9, fontWeight: 600, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' },
    td: { padding: '5px 8px', fontSize: 10, color: '#1e293b', borderBottom: '1px solid #f1f5f9', textAlign: 'right', whiteSpace: 'nowrap' },
    tdDesc: { padding: '5px 8px', fontSize: 10, color: '#1e293b', borderBottom: '1px solid #f1f5f9', textAlign: 'left', fontWeight: 500 },
    tdWarn: { padding: '5px 8px', fontSize: 10, color: '#1e293b', borderBottom: '1px solid #f1f5f9', textAlign: 'right', whiteSpace: 'nowrap', background: '#fef9c3' },
    tfoot: { padding: '5px 8px', fontSize: 10, fontWeight: 700, color: '#0f172a', borderTop: '2px solid #e2e8f0', background: '#f8fafc', textAlign: 'right', whiteSpace: 'nowrap' },
};

// Job-level Actual PF, same rule as the rows and the Overview total: 1.00 until the job is
// 25% earned (total earned / total budget hrs), then total earned / total actual hrs.
const totalActualPf = (t) => {
    const budget = pNum(t.totalBudgetHoursTotal), earned = pNum(t.totalEarnedHoursTotal), actual = pNum(t.totalActualHoursTotal)
    if (budget > 0 && earned / budget * 100 < 25) return fNum(1)
    return actual > 0 ? fNum(earned / actual) : ''
}

const ProductivityCombined = memo(forwardRef(({ setJobRef, photo, job, setActualHoursRef, setBudgetHoursRef, setUpdatedBudgetHoursRef, setEacHoursRef, collapsedParent, overview,
    // Overview VoltaGrid: Equipment and Electrical open one level to the bid's WBS groups, as the budget table does
    ovVoltaGrid,
    // Props passed in overview mode to avoid circular atom updates
    ovJobUserData, ovJobCostDetail, ovJobPhaseCodes, ovSelectedJobData, ovSelectedJob, ovSelectedVersion, ovProjectComplete, ovVersionInfo, ovRegular, ovLockedVersion
}, ref) => {
    const [phases, setPhases] = useState([{}, {}, {}])
    const [phaseDescriptions, setPhaseDescriptions] = useState([])
    const [etcDollars, setEtcDollars] = useState({})
    const [etcHours, setEtcHours] = useState({})
    // In overview mode, use props instead of atoms to avoid circular updates
    const [_selectedJob] = useAtom(selectedJobAtom);
    const [_selectedVersion] = useAtom(selectedVersionAtom);
    const [_selectedJobData] = useAtom(selectedJobDataAtom);
    const [_jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [_jobCostDetail] = useAtom(jobCostDetailAtom);
    const [isLiveVersion] = useAtom(isLiveVersionAtom);
    const [_regular] = useAtom(phasedForecastAtom);
    const [_projectComplete] = useAtom(projectCompleteAtom);
    const [_versions] = useAtom(dataSourceVersionAtom)
    const [_lockedVersion] = useAtom(selectedVersionLockedAtom)
    const [actualHoursValue, setActualHoursValue] = useAtom(actualHoursAtom);
    const [budgetHoursValue, setBudgetHoursValue] = useAtom(budgetHoursAtom);
    const [updatedBudgetHoursValue, setUpdatedBudgetHoursValue] = useAtom(updatedBudgetHoursAtom);
    const [eacHoursValue, setEacHoursValue] = useAtom(eacHoursAtom);

    const selectedJob = overview ? ovSelectedJob : _selectedJob;
    const selectedVersion = overview ? ovSelectedVersion : _selectedVersion;
    const selectedJobData = overview ? ovSelectedJobData : _selectedJobData;
    const jobUserData = overview ? ovJobUserData : _jobUserData;
    const jobCostDetail = overview ? ovJobCostDetail : _jobCostDetail;
    const regular = overview ? ovRegular : _regular;
    const projectComplete = overview ? ovProjectComplete : _projectComplete;
    const versions = overview ? [] : _versions;
    const lockedVersion = overview ? ovLockedVersion : _lockedVersion;

    const [phaseDayZero, setPhaseDayZero] = useState({})
    const [phaseCO, setPhaseCO] = useState({})
    const [projectWeeks, setProjectWeeks] = useState([])
    const [projectDates, setProjectDates] = useState([])
    const [userNotes, setUserNotes] = useState('')
    const [userData, setUserData] = useState({})
    const localChanges = useRef([])
    const focusedId = useRef(null)
    const [versionInfo, setVersionInfo] = useState({})
    const rowsData = useRef({})
    const [localFocusedId, setLocalFocusedId] = useState()
    const [textTimeout, setTextTimeout] = useState(null)
    const [disciplines, setDisciplines] = useState({})
    const disciplinesRef = useRef({})
    // 'user' = free-text phase->discipline mapping (current behavior);
    // 'standard' = auto-map from the standard WBS data dictionary (Area 1-4 cost codes)
    const [disciplineMode, setDisciplineMode] = useState('user')
    const inputsRef = useRef({})
    const [sections, setSections] = useState([])
    const [math, setMath] = useState({})
    const [mathRow, setMathRow] = useState({})
    const [pfExplanations, setPfExplanations] = useState({})
    // VoltaGrid only: the user's own wording for a cost code, in place of JDE's
    // (Seth 10/27: "let the user change the cost code descriptions on the
    // productivity tab (just for the voltagrid jobs)")
    const [descriptions, setDescriptions] = useState({})
    const descriptionTimers = useRef({})
    // const [expanded, setExpanded] = useState(true);

    // useEffect(() => {
    //     if (photo) {
    //         setExpanded(true);
    //         const timer = setTimeout(() => {
    //             setExpanded(false);
    //         }, 10000);
    //         return () => clearTimeout(timer);
    //     }
    // }, [photo]);
    const [expanded, setExpanded] = useState(overview ? false : photo || collapsedParent ? false : true)
    const [_jobPhaseCodes] = useAtom(jobPhaseCodesAtom);
    const jobPhaseCodes = overview ? ovJobPhaseCodes : _jobPhaseCodes;
    const totalHoursRef = useRef({})
    const totalTrendEACRef = useRef({})
    const totalPMEACRef = useRef({})
    const totalTotalHours = useRef({})
    const [pageWidth, setPageWidth] = useState(window.innerWidth);
    const changeListRef = useRef([])

    const productivityRef = useRef()
    if (photo) {
        setJobRef(job, 'productivity', productivityRef)
    }

    useEffect(() => {
        if (!photo && !overview) {
            console.log('collapsed parent', collapsedParent)
            setExpanded(!collapsedParent)
        }
    }, [collapsedParent])

    const handlePhaseRowData = (i, newRowData) => {
        const copyData = JSON.parse(JSON.stringify(rowsData.current));
        copyData[i] = newRowData;
        rowsData.current = copyData;
    };

    const savePFData = () => {
        const rows = rowsData.current;
        if (!rows || !selectedJob || !selectedVersion) return;
        Object.values(rows).forEach(row => {
            const discipline = row['Discipline Header'];
            if (!discipline) return;
            const actualPF = row['Actual PF'] || '';
            const pmPF = row['PM Forecasted PF'] || '';
            if (!actualPF && !pmPF) return;
            updateUserDataArray({
                userId: '154045',
                userName: 'Seth Bailey',
                fieldName: discipline,
                tab: 'PF',
                rowId: '1',
                version: selectedVersion + '',
                value: JSON.stringify({
                    actualPF,
                    pmPF,
                    budgetHours: row['Budget Hours'] || 0,
                    actualHours: row['Actual Hours'] || 0,
                    earnedHours: row['Earned Hours'] || 0,
                    pmEACHours: row['PM EAC Hours'] || 0
                }),
                id: selectedJob,
                lockedVersion: lockedVersion
            });
        });
    };

    useImperativeHandle(ref, () => ({
        createExcel,
        savePFData
    }));

    const createExcel = async () => {
        try {
            // Discipline header rows are keyed by their numeric section index; phase
            // subrows are keyed "<section>-<phase>". Split them into two tabs: the
            // discipline rollups on one sheet, the cost-code (phase) detail on another.
            const isPhaseKey = (k) => typeof k === 'string' && k.includes('-');
            const entries = Object.entries(rowsData.current);
            const disciplineEntries = entries
                .filter(([k]) => !isPhaseKey(k))
                .sort((a, b) => Number(a[0]) - Number(b[0]));
            const phaseEntries = entries
                .filter(([k]) => isPhaseKey(k))
                .sort((a, b) => {
                    const [ai, aj] = a[0].split('-').map(Number);
                    const [bi, bj] = b[0].split('-').map(Number);
                    return (ai - bi) || (aj - bj);
                });

            // Values come through as comma-formatted strings (e.g. "1,234.5"); convert
            // the numeric columns back to real numbers so the workbook stores them as
            // numbers and the hours columns total correctly. Blanks stay blank.
            const num = (v) => (v === '' || v === null || v === undefined) ? '' : pNum(v);
            const metrics = (row) => ({
                'Original Budget QTY': num(row['Original Budget QTY']),
                'Revised Budget QTY': num(row['Revised Budget QTY']),
                'Installed QTY': num(row['Installed QTY']),
                'Installed % Comp': num(row['Installed % Comp']),
                'Budget Hrs/Qty': num(row['Budget Hrs/Qty']),
                'Actual Hrs/Qty': num(row['Actual Hrs/Qty']),
                'PM Forecasted Hrs/Qty': num(row['PM Forecasted Hrs/Qty']),
                'Actual PF': num(row['Actual PF']),
                'PM Forecasted PF': num(row['PM Forecasted PF']),
                'Actual Hours': num(row['Actual Hours']),
                'Earned Hours': num(row['Earned Hours']),
                'Budget Hours': num(row['Budget Hours']),
                'Trending EAC Hours': num(row['Trending EAC Hours']),
                'PM EAC Hours': num(row['PM EAC Hours']),
            });

            const disciplineRows = disciplineEntries.map(([, row]) => ({
                'Discipline Header': row['Discipline Header'],
                UOM: row.UOM,
                ...metrics(row),
            }));

            const costCodeRows = phaseEntries.map(([, row]) => ({
                Discipline: row.Discipline,
                'Phase Code': row['Phase Code'],
                Description: row.Description,
                UOM: row.UOM,
                ...metrics(row),
            }));

            const ts = versionInfo?.Version_Start_DateTime
                ? moment(versionInfo.Version_Start_DateTime).format('M-D-YYYY') : '';
            const metadata = {
                jobId: selectedJob || '',
                jobName: selectedJobData?.BU_Description || selectedJobData?.Job_Description || '',
                versionTimestamp: ts,
            };

            const response = await fetch('https://forecastingtestpythonservice.azurewebsites.net/api/download_productivity', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ disciplineRows, costCodeRows, metadata }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const filename = ['Productivity', selectedJob, ts].filter(Boolean).join('_') + '.xlsx';

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error:', error);
        }
    }

    useEffect(() => {
        if (overview) {
            if (ovVersionInfo) setVersionInfo(ovVersionInfo);
        } else {
            versions && versions.length > 0 && setVersionInfo(versions.find(v => v.Version_GUID === selectedVersion))
        }
    }, [overview, ovVersionInfo, versions, selectedVersion]);

    useEffect(() => {
        if (selectedJob && selectedVersion && !overview) {
            loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion }).then((data) => {
                setJobUserData(data?.data);
            })
        }
    }, [selectedJob, selectedVersion])

    useEffect(() => {
        let completeDate = ''
        const uData = {
            dayZero: {},
            cos: {},
            burnTool: {},
            burnToolPhased: {},
            scorecard: {},
            productivity: {},
            math: {},
            mathRow: {},
            pfExplanation: {},
            description: {}
        }
        ////console.log('user data', jobUserData)
        if (jobUserData && jobUserData.length > 0) {
            let totalHours
            jobUserData && jobUserData.forEach((d) => {
                if (d?.Forecast_Tab === 'Burn Tool Labor') {
                    if (d.Field_Name === 'Total_Hours') {
                        totalHours = totalHours + (pNum(d.Forecast_Value) ? pNum(d.Forecast_String) : 0)
                    }
                    if (uData.burnTool[d?.Forecast_Row_ID]) {
                        uData.burnTool[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    } else {
                        uData.burnTool[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }
                }

                if (d.Forecast_Field_Name === 'completionDateForecast') {
                    completeDate = d.Forecast_Value
                }

                if (d?.Forecast_Tab === 'Burn Tool Labor Phased') {
                    uData.burnToolPhased[d?.Forecast_Row_ID] = d
                }

                if (d?.Forecast_Tab === 'Day Zero Budget') {
                    uData.dayZero[d?.Forecast_Row_ID] = d
                }

                if (d?.Forecast_Tab === 'Change Order') {
                    uData.cos[d?.Forecast_User_Data_GUID || d?.Forecast_Row_ID] = { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label }
                }

                if (d?.Forecast_Tab === 'Scorecard') {
                    if (uData.scorecard[d?.Forecast_Row_ID]) {
                        uData.scorecard[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    } else {
                        uData.scorecard[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }
                }

                if (d?.Forecast_Tab === 'Productivity Tracker') {
                    if (d?.Forecast_Field_Name === 'discipline') {
                        uData.productivity[d?.Forecast_Row_ID] = d.Forecast_Value
                    } else if (d?.Forecast_Field_Name === 'math') {
                        uData.math[d?.Forecast_Row_ID] = d.Forecast_Value
                    } else if (d?.Forecast_Field_Name === 'mathRow') {
                        uData.mathRow[d?.Forecast_Row_ID] = d.Forecast_Value
                    } else if (d?.Forecast_Field_Name === 'pfExplanation') {
                        uData.pfExplanation[d?.Forecast_Row_ID] = d.Forecast_Value
                    } else if (d?.Forecast_Field_Name === 'description') {
                        uData.description[d?.Forecast_Row_ID] = d.Forecast_Value
                    } else if (d?.Forecast_Field_Name === 'disciplineMode') {
                        setDisciplineMode(d.Forecast_Value === 'standard' ? 'standard' : 'user')
                    } else if (d?.Forecast_Field_Name === 'notes') {
                        setUserNotes(d.Forecast_Value)
                    }
                }

            })

            const costTypeDayZero = {}
            Object.values(uData.dayZero).forEach((row) => {
                ////console.log('dayZero', row)
                if (costTypeDayZero[row['Cost_Code']]) {
                    if (row['Cost_Type'] && row['Cost_Type'][0] == '5' && row['Cost_Type'][1] == '1' && row['Cost_Type'][2] != '8') {
                        costTypeDayZero[row['Cost_Code']].hours += pNum(row['Hours']) ? pNum(row['Hours']) : 0
                        costTypeDayZero[row['Cost_Code']].dollars += pNum(row['Value_Amount']) ? pNum(row['Value_Amount']) : 0
                    }
                    costTypeDayZero[row['Cost_Code']].uom = row['UoM']
                    costTypeDayZero[row['Cost_Code']].quantities = costTypeDayZero[row['Cost_Code']].quantities + (isNaN(pNum(row['Quantity'])) ? 0 : pNum(row['Quantity']))
                } else {
                    costTypeDayZero[row['Cost_Code']] = {
                        hours: row['Cost_Type'] && row['Cost_Type'][0] == '5' && row['Cost_Type'][1] == '1' && row['Cost_Type'][2] != '8' && row['Cost_Code'] ? pNum(row['Hours']) ? pNum(row['Hours']) : 0 : 0,
                        dollars: row['Cost_Type'] && row['Cost_Type'][0] == '5' && row['Cost_Type'][1] == '1' && row['Cost_Type'][2] != '8' && row['Cost_Code'] ? pNum(row['Value_Amount']) ? pNum(row['Value_Amount']) : 0 : 0,
                        uom: row['UoM'] ? row['UoM'] : '',
                        quantities: row['Quantity'] ? (isNaN(pNum(row['Quantity'])) ? 0 : pNum(row['Quantity'])) : 0
                    }
                }
            })
            console.log('costTypeDayZero', costTypeDayZero)

            const costTypeCos = {}
            Object.values(uData.cos).forEach((row) => {
                let phase = row.Cost_Code
                const isLaborHours = row.Forecast_Field_Name === 'totalLaborHours' ||
                    (String(row.Cost_Type || '').trim() === '51100' && row.Forecast_Field_Name !== 'totalEquipmentHours')
                const laborHours = isLaborHours ? (isNaN(pNum(row.Hours)) ? 0 : pNum(row.Hours)) : 0
                if (costTypeCos[phase] && row.Status == 'Approved') {
                    costTypeCos[phase].hours = costTypeCos[phase].hours + laborHours
                    costTypeCos[phase].budgetQty = costTypeCos[phase].budgetQty + (isNaN(pNum(row.Quantity)) ? 0 : pNum(row.Quantity))
                    costTypeCos[phase].dollars = costTypeCos[phase].dollars
                        + (isNaN(pNum(row['51100']) ? 0 : pNum(row['51100'])))
                        + (isNaN(pNum(row['51200']) ? 0 : pNum(row['51200'])))
                        + (isNaN(pNum(row['51300']) ? 0 : pNum(row['51300'])))
                        + (isNaN(pNum(row['51350']) ? 0 : pNum(row['51350'])))
                        + (isNaN(pNum(row['51400']) ? 0 : pNum(row['51400'])))
                        + (isNaN(pNum(row['51500']) ? 0 : pNum(row['51500'])))
                        + (isNaN(pNum(row['51600']) ? 0 : pNum(row['51600'])))
                        + (isNaN(pNum(row['51700']) ? 0 : pNum(row['51700'])))

                } else if (phase && row.Status == 'Approved') {
                    costTypeCos[phase] = {
                        hours: laborHours,
                        budgetQty: pNum(row.Quantity) ? (isNaN(pNum(row.Quantity)) ? 0 : pNum(row.Quantity)) : 0,
                        dollars: (isNaN(pNum(row['51100']) ? 0 : pNum(row['51100'])))
                            + (isNaN(pNum(row['51200']) ? 0 : pNum(row['51200'])))
                            + (isNaN(pNum(row['51300']) ? 0 : pNum(row['51300'])))
                            + (isNaN(pNum(row['51350']) ? 0 : pNum(row['51350'])))
                            + (isNaN(pNum(row['51400']) ? 0 : pNum(row['51400'])))
                            + (isNaN(pNum(row['51500']) ? 0 : pNum(row['51500'])))
                            + (isNaN(pNum(row['51600']) ? 0 : pNum(row['51600'])))
                            + (isNaN(pNum(row['51700']) ? 0 : pNum(row['51700'])))
                    }
                }
            })

            console.log('costTypeCos', costTypeCos, uData.cos)

            const lHours = {}
            const lDollars = {}
            Object.keys(uData.burnToolPhased).forEach((row) => {
                const phasedLabor = uData.burnToolPhased
                const weekRow = row.includes('=')
                const week = row.split('=')[1]
                const phase = row.split('=')[0]
                const phaseDetails = phase ? phasedLabor[phase] : {}
                const pc = projectComplete ? projectComplete : completeDate
                const withinProject = moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(pc))
                const withinStartStop = moment(week).isAfter(moment(phaseDetails?.Phase_Start).startOf('week')) && moment(week).isBefore(moment(phaseDetails?.Phase_End).endOf('week'))
                const h = phasedLabor[row]?.Number_Hours ? phasedLabor[row].Number_Hours : 0
                const bR = phasedLabor[row]?.Rate ? phasedLabor[row].Rate : 0
                const cS = phasedLabor[row] && phasedLabor[row]?.Number_Units ? phasedLabor[row].Number_Units : 0
                //console.log('phasedLabor true?', versionInfo, projectComplete)
                if (weekRow && phasedLabor[row] && h && bR && cS && withinProject){// && withinStartStop) {
                    if (lDollars[phase]) {
                        lDollars[phase] = lDollars[phase] + (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(bR) * pNum(cS) : 0)
                    } else {
                        lDollars[phase] = (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(bR) * pNum(cS) : 0)
                    }

                    if (lHours[phase]) {
                        lHours[phase] = lHours[phase] + (!isNaN(pNum(h)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                    } else {
                        lHours[phase] = (!isNaN(pNum(h)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                    }
                }
            })



            console.log('seth', lDollars, lHours)
            // Set even when empty: a job with no phased crew in the forecast
            // window has no ETC hours. Keeping the last ones put the previous
            // job's ETC on this job's phases, so the Productivity tab showed
            // 54026071's 35,490 h on 54026070's Project Team while the Overview,
            // mounted fresh per job, showed none (Seth 10/22).
            setEtcDollars(lDollars)
            setEtcHours(lHours)
            setPhaseDayZero(costTypeDayZero)
            setPhaseCO(costTypeCos)
            ////console.log('uData', uData.scorecard)
            setUserData(uData.scorecard)
            ////console.log('set disciplines', uData.productivity)
            setDisciplines(uData.productivity)
            setMath(uData.math)
            setMathRow(uData.mathRow)
            setPfExplanations(uData.pfExplanation)
            setDescriptions(uData.description)
        } else {
            setDescriptions({})
        }
    }, [jobUserData, versionInfo, projectComplete])

    useEffect(() => {
        let dateSections = {}
        let uniqueWeeks = {}
        const plannedStart = moment(selectedJobData?.Planned_Start_Date)
        const plannedFinish = moment(selectedJobData?.Planned_Complete_Date)

        let currentDay = plannedStart
        let currentWeek = plannedStart.startOf('isoWeek')
        const days = plannedFinish.diff(plannedStart, 'days')
        for (let i = 0; i < days; i++) {
            const day = currentDay.clone()
            const week = currentDay.clone()
            day.add(i, 'days')
            week.add(i, 'days')
            currentWeek = week.startOf('isoWeek')
            const currentDayFormatted = day.format('YYYY-MM-DD')
            uniqueWeeks[currentWeek.format('YYYY-MM-DD')] = 1
            dateSections[currentDayFormatted] = [currentWeek.format('YYYY-MM-DD')]
        }

        setProjectDates(dateSections)
        setProjectWeeks(Object.keys(uniqueWeeks))
    }, [selectedJobData])

    useEffect(() => {
        ////console.log('phases', phaseDayZero, phaseCO)
        const phases = {}
        //console.log('jobCostDetail', jobCostDetail.filter()
        Array.isArray(jobCostDetail) ? jobCostDetail?.forEach((row) => {
            if (!phases[row.Phase_Code] && row.Phase_Code) {
                phases[row.Phase_Code] = [row]
            } else if (row.Phase_Code) {
                phases[row.Phase_Code].push(row)
            }
        }) : null

        Object.keys(phaseDayZero).forEach((p) => {
            if (!phases[p]) {
                phases[p] = [{ Phase_Code: p, Phase_Code_Description: jobPhaseCodes[p] }]
            }
        })

        Object.keys(phaseCO).forEach((p) => {
            if (!phases[p]) {
                phases[p] = [{ Phase_Code: p, Phase_Code_Description: jobPhaseCodes[p] }]
            }
        })

        // Calculate Heavy Job installed quantities from FP document type
        const heavyJobInstalledByPhase = {}
        Array.isArray(jobCostDetail) ? jobCostDetail?.forEach((row) => {
            if (row.Document_Type == 'FP' && row.Phase_Code) {
                if (!heavyJobInstalledByPhase[row.Phase_Code]) {
                    heavyJobInstalledByPhase[row.Phase_Code] = 0
                }
                heavyJobInstalledByPhase[row.Phase_Code] += pNum(row.Total_Hours) || 0
            }
        }) : null

        const ph = Object.keys(phases)
        const phDescriptions = []
        const isVg = isVoltaGridJob(selectedJob)
        ph.forEach((p) => {
            const code = phases[p][0].Phase_Code
            if (code != '1000' && code != '899999' && code != '899998' && code != '990000') {
                const jdeDescription = phases[p][0].Phase_Code_Description
                phDescriptions.push({
                    description: (isVg && String(descriptions[code] || '').trim()) || jdeDescription,
                    jdeDescription,
                    code: phases[p][0].Phase_Code,
                    dayZero: phaseDayZero[code]?.hours ? fNum(pNum(phaseDayZero[code]?.hours)) : 0,
                    cos: phaseCO[code]?.hours ? fNum(pNum(phaseCO[code]?.hours)) : 0,
                    dayZeroDollars: phaseDayZero[code]?.dollars ? fNum(pNum(phaseDayZero[code]?.dollars)) : 0,
                    dayZeroHours: phaseDayZero[code]?.hours ? fNum(pNum(phaseDayZero[code]?.hours)) : 0,
                    coDollars: phaseCO[code]?.dollars ? fNum(pNum(phaseCO[code]?.dollars)) : 0,
                    coHours: phaseCO[code]?.hours ? fNum(pNum(phaseCO[code]?.hours)) : 0,
                    dayZeroQuantity: phaseDayZero[code]?.quantities ? fNum(pNum(phaseDayZero[code]?.quantities)) : 0,
                    coQuantity: phaseCO[code]?.budgetQty ? fNum(pNum(phaseCO[code]?.budgetQty)) : 0,
                    heavyJobInstalled: heavyJobInstalledByPhase[code] || 0
                })
            }
        })
        ////console.log('phases', phases, jobPhaseCodes, phDescriptions)
        setPhases(phases)
        setPhaseDescriptions(phDescriptions)
    }, [jobCostDetail, phaseCO, phaseDayZero, etcDollars, jobPhaseCodes, descriptions, selectedJob])

    const PhaseRow = (props) => {
        console.log('render phase row', props, userData[props.phase?.code], etcHours[props.phase?.code])
        const index = props.index
        const identifier = props.identifier
        const onSendToParent = props.onSendData
        let phaseActualHour = 0
        let phaseActualDollar = 0
        const rows = phases[props.phase?.code] ? phases[props.phase?.code] : []
        Array.isArray(rows) ? rows?.forEach((row) => {
            if (row.Cost_Type_Code && row.Cost_Type_Code[0] == '5' && row.Cost_Type_Code[1] == '1' && row.Cost_Type_Code[2] != '8') {
                phaseActualDollar += pNum(row.Job_Cost)
                phaseActualHour += pNum(row.Total_Hours)
            }
        }) : null
        const phase = props.phase?.code
        const [expandEst, setExpandEst] = useState(true)
        const [expandAct, setExpandAct] = useState(true)
        const [code, setCode] = useState(props.phase?.code)
        const [codeDescription, setCodeDescription] = useState(props.phase?.description)
        const [phaseWeeks, setPhaseWeeks] = useState([])
        const [phaseActualDollars, setPhaseActualDollars] = useState(fNum(phaseActualDollar))
        const [phaseActualHours, setPhaseActualHours] = useState(fNum(pNum(phaseActualHour)))
        const [dayZero, setDayZero] = useState(props.dayZero)
        const [co, setCo] = useState(props.cos)
        const [dayZeroDollars, setDayZeroDollars] = useState(props.phase?.dayZeroDollars)
        const [coDollars, setCoDollars] = useState(props.phase?.coDollars)
        const [dayZeroHours, setDayZeroHours] = useState(props.phase?.dayZeroHours)
        const [coHours, setCoHours] = useState(props.phase?.coHours)
        const [budgetDollars, setBudgetDollars] = useState(pNum(dayZeroDollars) + pNum(coDollars))
        const [budgetHours, setBudgetHours] = useState(pNum(dayZeroHours) + pNum(coHours))
        const [burnRateEst, setBurnRateEst] = useState(0)
        const [plug, setPlug] = useState(userData[props.phase?.code]?.plug)
        const [installed, setInstalled] = useState(userData[props.phase?.code]?.installed && !isNaN(pNum(userData[props.phase?.code]?.installed)) ? fNum(pNum(userData[props.phase?.code]?.installed)) : '')
        const heavyJobInstalled = props.phase?.heavyJobInstalled || 0
        const totalInstalledQuantity = (isNaN(pNum(heavyJobInstalled)) ? 0 : pNum(heavyJobInstalled)) + (isNaN(pNum(installed)) ? 0 : pNum(installed))
        const [uom, setUom] = useState(userData[props.phase?.code]?.uom)
        const [notes, setNotes] = useState(userData[props.phase?.code]?.notes)
        const [dayZeroQuantity, setDayZeroQuantity] = useState(props.phase?.dayZeroQuantity)
        const [coQuantity, setCoQuantity] = useState(props.phase?.coQuantity)
        const totalQuantity = (isNaN(pNum(dayZeroQuantity)) ? 0 : pNum(dayZeroQuantity)) + (isNaN(pNum(coQuantity)) ? 0 : pNum(coQuantity)) + (isNaN(pNum(plug)) ? 0 : pNum(plug))
        const remainingQuantity = totalQuantity > 0 ? (isNaN(pNum(totalQuantity)) ? 0 : pNum(totalQuantity)) - (isNaN(pNum(totalInstalledQuantity)) ? 0 : pNum(totalInstalledQuantity)) : ''
        const percentComplete = totalQuantity > 0 ? (isNaN(pNum(totalInstalledQuantity)) ? 0 : 100 * pNum(totalInstalledQuantity)) / (isNaN(pNum(totalQuantity)) ? 0 : pNum(totalQuantity)) : ''
        const [budgetDollarsQty, setBudgetDollarsQty] = useState(totalQuantity ? fNum(budgetDollars / totalQuantity) : '')
        // Keep the unrounded hrs/qty rates for the math below. The displayed rates are
        // rounded to 2 decimals, and on long LF runs (e.g. 200 hrs over 47,350 LF) the
        // actual rate rounds to "0.00", which blanked the PF and skewed trending EAC.
        const budgetHoursQtyRaw = totalQuantity ? budgetHours / totalQuantity : 0
        const budgetHoursQty = totalQuantity ? fNum(budgetHoursQtyRaw) : ''
        const [actualDollarsQty, setActualDollarsQty] = useState(!isNaN(pNum(totalQuantity)) && phaseActualDollar ? fNum(phaseActualDollar / pNum(totalQuantity)) : '')
        const actualHoursQtyRaw = totalInstalledQuantity && pNum(totalInstalledQuantity) > 0 ? percentComplete < 25 ? budgetHoursQtyRaw : pNum(phaseActualHours) / pNum(totalInstalledQuantity) : 0
        const actualHoursQtyValue = totalInstalledQuantity && pNum(totalInstalledQuantity) > 0 ? percentComplete < 25 ? budgetHoursQty : fNum(actualHoursQtyRaw) : '';
        const actualHoursQty = actualHoursQtyValue
        const forcastedRateDollars = etcDollars[phase] && remainingQuantity ? fNum(etcDollars[phase] / remainingQuantity) : ''
        ////console.log('forcastedRateDollars', etcHours[phase], phaseActualHours, totalQuantity)
        const forcastedRateHours = regular == 'regular' ?
            userData[phase]?.forecastedHrs && pNum(totalQuantity) > 0 ? fNum((pNum(userData[phase]?.forecastedHrs) + pNum(phaseActualHours)) / pNum(totalQuantity)) : '' : pNum(totalQuantity) > 0 ? fNum((pNum(etcHours[phase]) + pNum(phaseActualHours)) / pNum(totalQuantity)) : ''
        const forecastedDollars = etcDollars[phase] ? fNum(etcDollars[phase]) : ''
        const forecastedHours = regular == 'regular' ? userData[phase]?.forecastedHrs : etcHours[phase] ? fNum(pNum(etcHours[phase])) : ''
        const projectedDollars = percentComplete && pNum(percentComplete) > 25 ? actualHoursQtyRaw * pNum(remainingQuantity) : (dayZeroDollars + coDollars) > budgetDollars ? (dayZeroDollars + coDollars) - budgetDollars : ''
        const projectedHours = percentComplete && pNum(percentComplete) > 25 ? actualHoursQtyRaw * pNum(remainingQuantity) : (dayZeroHours + coHours) > budgetHours ? (dayZeroHours + coHours) - budgetHours : ''
        const bOrP = percentComplete && pNum(percentComplete) > 25 ? 'p' : ''
        const overUnderDollars = forecastedDollars && (forecastedDollars - projectedDollars) > 0 ? fNum(pNum(forecastedDollars) - pNum(projectedDollars), 1) : ''
        const overUnderHours = forecastedHours && (forecastedHours - projectedHours) > 0 ? fNum(pNum(forecastedHours) - pNum(projectedHours), 1) : ''
        const overUnderDollarsPercent = overUnderDollars && forecastedDollars ? fNum((pNum(overUnderDollars) / pNum(forecastedDollars)) * 100, 1) : ''
        const overUnderHoursPercent = overUnderHours && forecastedHours ? fNum((pNum(overUnderHours) / pNum(forecastedHours)) * 100, 1) : ''
        const pfDollars = budgetDollarsQty && budgetDollarsQty > 0 && actualDollarsQty ? fNum((pNum(actualDollarsQty) - pNum(budgetDollarsQty)) / pNum(budgetDollarsQty)) : ''
        // Actual PF matches the Overview tab: earned hrs / actual hrs, with % complete capped at 100%
        // and PF held at 1.00 until the phase is 25% complete (overview.js getCodeHoursData / pf).
        const pfEarnedHours = Math.min(pNum(percentComplete) || 0, 100) * pNum(budgetHours) / 100
        const pfPctComplete = pNum(budgetHours) > 0 ? pfEarnedHours / pNum(budgetHours) * 100 : 0
        const pfHours = pNum(budgetHours) > 0 ? (pfPctComplete < 25 ? fNum(1) : (pNum(phaseActualHours) > 0 ? fNum(pfEarnedHours / pNum(phaseActualHours)) : '')) : ''
        const pffDollars = budgetDollarsQty && budgetDollarsQty > 0 && forcastedRateDollars ? fNum((pNum(forcastedRateDollars) - pNum(budgetDollarsQty)) / pNum(budgetDollarsQty)) : ''
        const pffHours = budgetHoursQty && budgetHoursQty > 0 && forcastedRateHours ? fNum((pNum(forcastedRateHours) - pNum(budgetHoursQty)) / pNum(budgetHoursQty)) : ''
        //console.log('trendingeac', phase, percentComplete, actualHoursQty, totalQuantity, budgetHours, phaseActualHours)
        const trendingEACHours = percentComplete && pNum(percentComplete) >= 100 ? fNum(pNum(phaseActualHours), 1) : percentComplete && pNum(percentComplete) > 25 ? fNum(actualHoursQtyRaw * pNum(totalQuantity), 1) : fNum(pNum(budgetHours), 1)
        console.log('pm eac ', forecastedHours, phaseActualHours)
        const pmEACHours = fNum(pNum(forecastedHours) + pNum(phaseActualHours), 1)
        totalTrendEACRef.current[phase] = pNum(trendingEACHours)
        totalPMEACRef.current[phase] = pNum(pmEACHours)
        const pmPF = pmEACHours && pNum(pmEACHours) > 0 ? fNum(pNum(budgetHours) / pNum(pmEACHours)) : ''

        const earnedHours = pfEarnedHours

        const updateLocalChanges = async (field, value) => {
            console.log('saving scorecard data', field, value)
            if (selectedJob && value && value != 'undefined') {
                let data = {
                    userId: '154045',
                    userName: 'Seth Bailey',
                    fieldName: field,
                    tab: "Scorecard",
                    rowId: phase,
                    version: selectedVersion + '',
                    value: value + '',
                    id: selectedJob,
                    lockedVersion: lockedVersion
                }
                console.log('saving data', data)
                updateUserDataArray(data)

                const stateUpdate = {
                    Forecast_Field_Name: field,
                    Forecast_Value: value,
                    Forecast_Row_ID: phase,
                    Forecast_Tab: "Scorecard",
                    BU_ID: selectedJob,
                    key: phase
                }

                let isRecorded = false
                let lChanges = {}

                localChanges.current.forEach((d) => {
                    if (d.Forecast_Row_ID == phase && d.Forecast_Field_Name == field && d.Forecast_Tab == "Scorecard") {
                        isRecorded = true
                        d.Forecast_Value = value
                    }
                    lChanges[phase] = { ...d, key: d.Forecast_Row_ID + '*' + d.Forecast_Field_Name }
                })

                if (!isRecorded) {
                    lChanges[phase] = stateUpdate
                }

                localChanges.current = Object.values(lChanges)
            }
        }

        useEffect(() => {
            const rowData = {
                'Discipline': props.phase?.discipline ? props.phase.discipline : 'Other',
                'Description': codeDescription,
                'Phase Code': code,
                'UOM': uom,
                'Original Budget QTY': dayZeroQuantity,
                'Revised Budget QTY': pNum(dayZeroQuantity) + pNum(coQuantity),
                'Installed QTY': totalInstalledQuantity,
                'Installed % Comp': percentComplete,
                'Budget Hrs/Qty': budgetHoursQty,
                'Actual Hrs/Qty': actualHoursQty,
                'PM Forecasted Hrs/Qty': forcastedRateHours,
                'Actual PF': pfHours,
                'PM Forecasted PF': pmPF,
                'Actual Hours': phaseActualHours,
                'Forecasted Hours': forecastedHours,
                'Earned Hours': earnedHours,
                'Budget Hours': budgetHours,
                'Trending EAC Hours': trendingEACHours,
                'PM EAC Hours': pmEACHours,
            }

            onSendToParent(identifier, rowData);
        }, [props])

        if (overview) {
            const warnBg = jobCostDetail?.length > 0 && percentComplete > 0 && pNum(phaseActualHours) == 0;
            const warnActual = jobCostDetail?.length > 0 && ((percentComplete > 0 && pNum(phaseActualHours) == 0) || ((pNum(dayZeroQuantity) + pNum(coQuantity)) < 1 && pNum(phaseActualHours) > 0));
            return (
                <tr style={{ transition: 'background .15s' }} onMouseEnter={e => e.currentTarget.style.background='#f8fafc'} onMouseLeave={e => e.currentTarget.style.background=''}>
                    <td style={ovStyles.tdDesc}>{codeDescription}</td>
                    <td style={{ ...ovStyles.td, textAlign: 'center' }}>{uom}</td>
                    <td style={ovStyles.td}>{fNum(pNum(dayZeroQuantity) + pNum(coQuantity), 1)}</td>
                    <td style={ovStyles.td}>{totalInstalledQuantity > 0 ? fNum(pNum(totalInstalledQuantity), 1) : ''}</td>
                    <td style={warnBg ? ovStyles.tdWarn : ovStyles.td}>{percentComplete ? fNum(pNum(percentComplete), 1) + '%' : ''}</td>
                    <td style={ovStyles.td}>{budgetHoursQty}</td>
                    <td style={ovStyles.td}>{actualHoursQty}</td>
                    <td style={ovStyles.td}>{forcastedRateHours}</td>
                    <td style={ovStyles.td}>{pfHours}</td>
                    <td style={ovStyles.td}>{pmPF}</td>
                    <td style={warnActual ? ovStyles.tdWarn : ovStyles.td}>{fNum(pNum(phaseActualHours), 1)}</td>
                    <td style={ovStyles.td}>{fNum(pNum(earnedHours), 1)}</td>
                    <td style={ovStyles.td}>{fNum(pNum(budgetHours), 1)}</td>
                    <td style={warnBg ? ovStyles.tdWarn : ovStyles.td}>{trendingEACHours}</td>
                    <td style={ovStyles.td}>{pmEACHours}</td>
                </tr>
            );
        }

        return (
            <View style={{ flexDirection: 'row' }}>
                {overview ? null : <View style={s(100).cell}></View>}
                {overview ? null : <Text style={s(40).cell}>{index + 1}</Text>}
                <Text style={s(ovDesc).cell}>{codeDescription}</Text>
                {photo || overview ? null : <Text style={s(100).cell}>{code}</Text>}
                <Text style={s(50).cell}>{uom}</Text>
                {overview ? null : <Text style={s(100).cell}>{fNum(pNum(dayZeroQuantity), 1)}</Text>}
                <Text style={s(ov).cell}>{fNum(pNum(dayZeroQuantity) + pNum(coQuantity), 1)}</Text>
                <Text style={s(ov).cell}>{totalInstalledQuantity > 0 ? fNum(pNum(totalInstalledQuantity), 1) : ''}</Text>
                <Text style={[s(ov).cell,
                jobCostDetail?.length > 0 && percentComplete > 0 && pNum(phaseActualHours) == 0 ? { backgroundColor: 'yellow' } : null
                ]}>{percentComplete ? fNum(pNum(percentComplete), 1) + '%' : ''}</Text>
                <Text style={s(ov).cell}>{budgetHoursQty}</Text>
                <Text style={s(ov).cell}>{actualHoursQty}</Text>
                <Text style={s(ov).cell}>{forcastedRateHours}</Text>
                <Text style={s(ovPf).cell}>{pfHours}</Text>
                <Text style={s(ovPf).cell}>{pmPF}</Text>
                {renderPfExplanation(phase)}
                <Text style={[s(ov).cell,
                jobCostDetail?.length > 0 && ((percentComplete > 0 && pNum(phaseActualHours) == 0) || ((pNum(dayZeroQuantity) + pNum(coQuantity)) < 1 && pNum(phaseActualHours) > 0)) ? { backgroundColor: 'yellow' } : null
                ]}>{fNum(pNum(phaseActualHours), 1)}</Text>
                {/* <Text style={s(ov).cell}>{forecastedHours ? fNum(pNum(forecastedHours), 1) : ''}</Text> */}
                <Text style={s(ov).cell}>{fNum(pNum(earnedHours), 1)}</Text>
                <Text style={s(ov).cell}>{fNum(pNum(budgetHours), 1)}</Text>
                <Text style={[s(ov).cell,
                jobCostDetail?.length > 0 && percentComplete > 0 && pNum(phaseActualHours) == 0 ? { backgroundColor: 'yellow' } : null
                ]}>{trendingEACHours}</Text>
                <Text style={[s(ov).cell, {borderRight: '1px solid #aeadaf'}]}>{pmEACHours}</Text>
            </View >
        )
    }

    const DisciplineRow = (props) => {
        const index = props.index
        // A child row (a VoltaGrid WBS group under Equipment / Electrical) takes
        // its own phases and label and stays out of the totals.
        const isChild = !!props.childPhases
        // A discipline folded into another (AG Electrical Conduit under
        // Electrical) shows as a child row with its own figures; the parent
        // carries its hours in the totals.
        const sub = isChild || !!props.nested
        const discipline = isChild ? props.childLabel : props.phase?.discipline
        const sectionPhases = isChild ? props.childPhases : (sections && sections[index] ? sections[index] : [])
        // A VoltaGrid QPAC group counts the QPAC itself: 1 EA, so Budget Hrs/Qty
        // is the node's hours (9,315 a QPAC on the weighting workbook) and
        // Installed is the share of it earned. Its phases keep their own part
        // counts (Seth 10/9). Under Max it took its biggest phase count, 15.
        const qpacUnit = isChild && /^QPAC\s/i.test(String(props.childLabel || ''))
        // A discipline row takes its Math / Row pick from the Productivity tab, so
        // the Overview reads the same quantity as that tab (Seth 10/22). It no
        // longer counts the QPAC groups (12 EA) on its own: pick Qty "12 EA" on
        // the Productivity tab to show that on both.
        const [status, setStatus] = useState(math[discipline] ? math[discipline] : (qpacUnit ? 'Qty' : ''))
        const [mRow, setMRow] = useState(mathRow[discipline] ? mathRow[discipline] : (qpacUnit ? '1 EA' : ''))
        let uom = useState(userData[props.phase?.code]?.uom)
        let dayZeroQuantity = 0
        let coQuantity = 0
        let installed = 0
        let totalQuantity
        let percentComplete

        if (status && status == 'Row') {
            const row = sectionPhases[mRow - 1]
            if (row) {
                uom = userData[row.code]?.uom
                dayZeroQuantity = row?.dayZeroQuantity
                coQuantity = row?.coQuantity
                //installed = userData[row.code]?.installed && !isNaN(pNum(userData[row.code]?.installed)) ? fNum(userData[row.code]?.installed) : ''
                totalQuantity = (isNaN(pNum(dayZeroQuantity)) ? 0 : pNum(dayZeroQuantity)) + (isNaN(pNum(coQuantity)) ? 0 : pNum(coQuantity))
                //percentComplete = totalQuantity > 0 ? (isNaN(pNum(installed)) ? 0 : 100 * pNum(installed)) / (isNaN(pNum(totalQuantity)) ? 0 : pNum(totalQuantity)) : ''
            }

        } else if (status && status == 'Qty') {
            // The discipline's quantity is typed in the box ("11" or "11 EA"): for a
            // discipline whose phases each carry a per-unit rollup (the QPAC nodes),
            // no phase holds the count of units, so Max / Sum / Row can't show it.
            const q = parseQtyMath(mRow)
            const withUom = sectionPhases.find((p) => userData[p.code]?.uom)
            uom = q.uom || (withUom ? userData[withUom.code].uom : uom)
            dayZeroQuantity = q.qty
            coQuantity = 0
            totalQuantity = q.qty
        } else if (status && status == 'Sum') {
            sectionPhases.forEach((p) => {
                //installed += (isNaN(pNum(userData[p.code]?.installed)) ? 0 : pNum(userData[p.code]?.installed))
                dayZeroQuantity += (isNaN(pNum(p.dayZeroQuantity)) ? 0 : pNum(p.dayZeroQuantity))
                coQuantity += (isNaN(pNum(p.coQuantity)) ? 0 : pNum(p.coQuantity))
                ////console.log('sum', { installed, dayZeroQuantity, coQuantity })
            })
            totalQuantity = (isNaN(pNum(dayZeroQuantity)) ? 0 : pNum(dayZeroQuantity)) + (isNaN(pNum(coQuantity)) ? 0 : pNum(coQuantity))
            // The unit the phases carrying a quantity share, blank when they differ
            uom = sharedUnit(sectionPhases
                .filter((p) => (pNum(p.dayZeroQuantity) || 0) + (pNum(p.coQuantity) || 0) > 0)
                .map((p) => userData[p.code]?.uom))
            //percentComplete = totalQuantity > 0 ? (isNaN(pNum(installed)) ? 0 : 100 * pNum(installed)) / (isNaN(pNum(totalQuantity)) ? 0 : pNum(totalQuantity)) : ''
        } else { // default is Max
            let max = 0
            let maxIndex = 0
            sectionPhases.forEach((p, i) => {
                if (pNum(p.coQuantity) + pNum(p.dayZeroQuantity) > max) {
                    max = pNum(p.coQuantity) + pNum(p.dayZeroQuantity)
                    maxIndex = i
                }
            })
            let maxPhase = sectionPhases[maxIndex].code
            uom = userData[maxPhase]?.uom
            dayZeroQuantity = sectionPhases[maxIndex].dayZeroQuantity
            coQuantity = sectionPhases[maxIndex].coQuantity
            //installed = userData[maxPhase]?.installed && !isNaN(pNum(userData[maxPhase]?.installed)) ? fNum(userData[maxPhase]?.installed) : ''
            totalQuantity = (isNaN(pNum(dayZeroQuantity)) ? 0 : pNum(dayZeroQuantity)) + (isNaN(pNum(coQuantity)) ? 0 : pNum(coQuantity))
            //percentComplete = totalQuantity > 0 ? (isNaN(pNum(installed)) ? 0 : 100 * pNum(installed)) / (isNaN(pNum(totalQuantity)) ? 0 : pNum(totalQuantity)) : ''
        }

        const onSendToParent = props.onSendData

        let phaseActualHour = 0
        let phaseActualDollar = 0
        let dayZeroHours = 0
        let coHours = 0
        let budgetHours = 0
        let forecastedHours = 0
        let phaseActualHours = 0
        let earnedHours = 0
        let trendingEACHours = 0
        let pmEACSum = 0
        //let pmPF = 0

        const tally = (p) => {
            // Sum up Heavy Job installed quantities for this section
            let phaseActualHour = 0
            let phaseActualDollar = 0
            const rows = phases[p.code] ? phases[p.code] : []
            Array.isArray(rows) ? rows?.forEach((row) => {
                if (row.Cost_Type_Code && row.Cost_Type_Code[0] == '5' && row.Cost_Type_Code[1] == '1' && row.Cost_Type_Code[2] != '8') {
                    phaseActualDollar += pNum(row.Job_Cost)
                    phaseActualHour += pNum(row.Total_Hours)
                }
            }) : null

            let dZQuantity = p.dayZeroQuantity
            let coQuantity = p.coQuantity
            let installedPhase = userData[p.code]?.installed
            dayZeroHours = dayZeroHours + pNum(p.dayZeroHours)
            coHours = coHours + pNum(p.coHours)
            let pComplete = (dZQuantity + coQuantity) > 0 ? (isNaN(pNum(installedPhase)) ? 0 : pNum(installedPhase)) / (dZQuantity + coQuantity) : ''
            //earnedHours = earnedHours + (pComplete ? pNum(pComplete) * (pNum(p.dayZeroHours) + pNum(p.coHours)) : 0)
            ////console.log(p.code, 'earned hours', earnedHours,(pComplete ? pNum(pComplete) * (dayZeroHours + coHours) : 0), pComplete, dayZeroHours + coHours)
            phaseActualHours = phaseActualHours + phaseActualHour
            forecastedHours = regular == 'regular' ? forecastedHours + (isNaN(pNum(userData[p.code]?.forecastedHrs)) ? 0 : pNum(userData[p.code]?.forecastedHrs)) : forecastedHours + (isNaN(pNum(etcHours[p.code])) ? 0 : pNum(etcHours[p.code]))
            //installed = installed + (isNaN(pNum(installedPhase)) ? 0 : pNum(installedPhase))
            // Installed = manual Scorecard entry + HeavyJob installed (same as the PhaseRow and the Overview tab)
            const installedQ = (isNaN(pNum(userData[p?.code]?.installed)) ? 0 : pNum(userData[p?.code]?.installed)) + (isNaN(pNum(p.heavyJobInstalled)) ? 0 : pNum(p.heavyJobInstalled))
            const plug = userData[p?.code]?.plug
            const totalQuantityQ = (isNaN(pNum(p.dayZeroQuantity)) ? 0 : pNum(p.dayZeroQuantity)) + (isNaN(pNum(p.coQuantity)) ? 0 : pNum(p.coQuantity)) + (isNaN(pNum(plug)) ? 0 : pNum(plug))
            const dayZeroHoursQ = p?.dayZeroHours
            const coHoursQ = p?.coHours
            const budgetHoursQ = pNum(dayZeroHoursQ) + pNum(coHoursQ)
            const percentComplete = totalQuantityQ > 0 ? (isNaN(pNum(installedQ)) ? 0 : 100 * pNum(installedQ)) / (isNaN(pNum(totalQuantityQ)) ? 0 : pNum(totalQuantityQ)) : ''
            ////console.log('percent complete discipline', discipline, p.description, percentComplete, { totalQuantityQ, installedQ })
            earnedHours = earnedHours + (percentComplete ? Math.min(pNum(percentComplete), 100) * pNum(budgetHoursQ) / 100 : 0)
            //console.log('p', p.description, earnedHours, percentComplete, budgetHoursQ)

            const actualHoursQtyQ = installedQ && pNum(installedQ) > 0 ? pNum(phaseActualHour) / pNum(installedQ) : 0
            // Calculate per-phase trending EAC inline (matches PhaseRow formula) so it works even when PhaseRows aren't rendered (overview mode)
            const phaseTrendEAC = percentComplete && pNum(percentComplete) >= 100 ? pNum(phaseActualHour) : percentComplete && pNum(percentComplete) > 25 ? actualHoursQtyQ * pNum(totalQuantityQ) : pNum(budgetHoursQ)
            trendingEACHours = trendingEACHours + (totalTrendEACRef.current[p.code] ? totalTrendEACRef.current[p.code] : phaseTrendEAC)
            pmEACSum = pmEACSum + (totalPMEACRef.current[p.code] ? totalPMEACRef.current[p.code] : (pNum(phaseActualHour) + (regular == 'regular' ? (isNaN(pNum(userData[p.code]?.forecastedHrs)) ? 0 : pNum(userData[p.code]?.forecastedHrs)) : (isNaN(pNum(etcHours[p.code])) ? 0 : pNum(etcHours[p.code])))))
            ///pmPF = pmPF + (pmEACHours && pNum(pmEACHours) > 0 ? pNum(budgetHoursQ) / pNum(pmEACHours) : 0)
        }
        sectionPhases.forEach(tally)
        // The quantity columns (Installed, % Comp, Hours / Quantity) read the
        // section's own phases. The hours columns and PF also take in the
        // disciplines folded under it (Electrical with AG Electrical Conduit and
        // UG Electrical, Seth 10/22), whose quantities stay on their own rows.
        const own = {
            budgetHours: pNum(dayZeroHours) + pNum(coHours),
            earnedHours,
            actualHours: phaseActualHours,
            pmEACHours: pNum(phaseActualHours) + pNum(forecastedHours),
        }
        ;(props.foldedPhases || []).forEach(tally)

        budgetHours = pNum(dayZeroHours) + pNum(coHours)
        let budgetQty = pNum(dayZeroQuantity) + pNum(coQuantity)
        installed = own.budgetHours > 0 ? fNum(pNum(own.earnedHours) / own.budgetHours * pNum(totalQuantity)) : ''
        ////console.log('percentComplete', discipline, { earnedHours, budgetHours, totalQuantity, installed })
        ////console.log('percent complete discipline', discipline, { earnedHours, budgetHours, totalQuantity, installed })
        const totalInstalledQuantity = isNaN(pNum(installed)) ? 0 : pNum(installed)
        percentComplete = totalQuantity > 0 ? (isNaN(pNum(totalInstalledQuantity)) ? 0 : 100 * pNum(totalInstalledQuantity)) / (isNaN(pNum(totalQuantity)) ? 0 : pNum(totalQuantity)) : ''
        const remainingQuantity = totalQuantity > 0 ? (isNaN(pNum(totalQuantity)) ? 0 : pNum(totalQuantity)) - (isNaN(pNum(installed)) ? 0 : pNum(installed)) : ''
        // Unrounded rates for the math; the displayed rates are rounded to 2 decimals (see PhaseRow).
        const budgetHoursQtyRaw = totalQuantity ? own.budgetHours / pNum(totalQuantity) : 0
        const budgetHoursQty = totalQuantity ? fNum(budgetHoursQtyRaw) : ''
        const actualHoursQtyRaw = installed && pNum(installed) > 0 && own.actualHours ? percentComplete < 25 ? budgetHoursQtyRaw : pNum(own.actualHours) / pNum(installed) : 0
        const actualHoursQtyValue = installed && pNum(installed) > 0 && own.actualHours ? percentComplete < 25 ? budgetHoursQty : fNum(actualHoursQtyRaw) : '';
        const actualHoursQty = actualHoursQtyValue
        const projectedHours = percentComplete && pNum(percentComplete) > .25 ? actualHoursQtyRaw * pNum(remainingQuantity) : own.budgetHours - own.actualHours
        //const trendingEACHours = fNum(pNum(projectedHours) + pNum(phaseActualHours), 1)
        //console.log('pmeachours', discipline, phaseActualHours, forecastedHours, etcHours, userData, sectionPhases )
        const pmEACHours = fNum(pNum(phaseActualHours) + pNum(forecastedHours), 1)
        ////console.log('forecasted pmpf', discipline, budgetQty, pmEACHours)
        const forcastedRateHours = pNum(budgetQty) > 0 ? fNum(own.pmEACHours / pNum(budgetQty)) : 0
        //const forcastedRateHours = forecastedHours && remainingQuantity ? fNum(pNum(forecastedHours) / pNum(remainingQuantity)) : ''
        ////console.log('forecasted pmpf', discipline, { projectedHours, phaseActualHours })
        // Discipline PF matches the Overview tab: total earned hrs / total actual hrs (not an average of phase PFs),
        // held at 1.00 until the discipline is 25% complete.
        const pfPctComplete = pNum(budgetHours) > 0 ? pNum(earnedHours) / pNum(budgetHours) * 100 : 0
        const pfHours = pNum(budgetHours) > 0 ? (pfPctComplete < 25 ? fNum(1) : (pNum(phaseActualHours) > 0 ? fNum(pNum(earnedHours) / pNum(phaseActualHours)) : '')) : ''

        const pmPF = pmEACHours && pNum(pmEACHours) > 0 ? fNum(pNum(budgetHours) / pNum(pmEACHours)) : ''


        const phase = props.phase?.code
        const [code, setCode] = useState(props.phase?.code)
        const [codeDescription, setCodeDescription] = useState(props.phase?.description)
        const [dayZeroDollars, setDayZeroDollars] = useState(props.phase?.dayZeroDollars)
        const [coDollars, setCoDollars] = useState(props.phase?.coDollars)
        const [budgetDollars, setBudgetDollars] = useState(pNum(dayZeroDollars) + pNum(coDollars))
        const [budgetDollarsQty, setBudgetDollarsQty] = useState(totalQuantity ? fNum(budgetDollars / totalQuantity) : '')
        const [actualDollarsQty, setActualDollarsQty] = useState(!isNaN(pNum(totalQuantity)) && phaseActualDollar ? fNum(phaseActualDollar / pNum(totalQuantity)) : '')
        const forcastedRateDollars = etcDollars[phase] && remainingQuantity ? fNum(etcDollars[phase] / remainingQuantity) : ''
        const forecastedDollars = etcDollars[phase] ? fNum(etcDollars[phase]) : ''
        const projectedDollars = percentComplete && pNum(percentComplete) > 25 ? pNum(actualHoursQty) * pNum(remainingQuantity) : (dayZeroDollars + coDollars) > budgetDollars ? (dayZeroDollars + coDollars) - budgetDollars : ''
        const bOrP = percentComplete && pNum(percentComplete) > 25 ? 'p' : ''
        const overUnderDollars = forecastedDollars && (forecastedDollars - projectedDollars) > 0 ? fNum(pNum(forecastedDollars) - pNum(projectedDollars), 1) : ''
        const overUnderHours = forecastedHours && (forecastedHours - projectedHours) > 0 ? fNum(pNum(forecastedHours) - pNum(projectedHours), 1) : ''
        const overUnderDollarsPercent = overUnderDollars && forecastedDollars ? fNum((pNum(overUnderDollars) / pNum(forecastedDollars)) * 100, 1) : ''
        const overUnderHoursPercent = overUnderHours && forecastedHours ? fNum((pNum(overUnderHours) / pNum(forecastedHours)) * 100, 1) : ''
        const pfDollars = budgetDollarsQty && budgetDollarsQty > 0 && actualDollarsQty ? fNum((pNum(actualDollarsQty) - pNum(budgetDollarsQty)) / pNum(budgetDollarsQty)) : ''
        const pffHours = budgetHoursQty && budgetHoursQty > 0 && forcastedRateHours ? fNum((pNum(forcastedRateHours) - pNum(budgetHoursQty)) / pNum(budgetHoursQty)) : ''
        const [isFocus, setIsFocus] = useState(false)

        const copyTotalHours = JSON.parse(JSON.stringify(totalHoursRef.current))
        if (!sub) copyTotalHours[discipline] = {
            totalActualHoursTotal: phaseActualHours,
            totalEarnedHoursTotal: earnedHours,
            totalBudgetHoursTotal: budgetHours,
            totalEACHoursTotal: trendingEACHours,
            totalPMEACHoursTotal: pmEACHours
        }

        const total = {
            totalActualPf: 0, // actual PF
            totalPf: 0, // PM PF
            totalPfCount: 0, // for average PF calculation
            totalActualHoursTotal: 0,
            totalEarnedHoursTotal: 0,
            totalBudgetHoursTotal: 0,
            totalEACHoursTotal: 0,
            totalPMEACHoursTotal: 0
        }
        Object.values(copyTotalHours).forEach((discipline) => {
            total.totalActualPf = total.totalActualPf + pNum(pfHours)
            total.totalPf = total.totalPf + pNum(pmPF)
            total.totalPfCount = total.totalPfCount ? total.totalPfCount + 1 : 1 // for average PF calculation
            total.totalActualHoursTotal = total.totalActualHoursTotal + pNum(discipline.totalActualHoursTotal)
            total.totalEarnedHoursTotal = total.totalEarnedHoursTotal + pNum(discipline.totalEarnedHoursTotal)
            total.totalBudgetHoursTotal = total.totalBudgetHoursTotal + pNum(discipline.totalBudgetHoursTotal)
            total.totalEACHoursTotal = total.totalEACHoursTotal + pNum(discipline.totalEACHoursTotal)
            total.totalPMEACHoursTotal = total.totalPMEACHoursTotal + pNum(discipline.totalPMEACHoursTotal)
        })

        //console.log('copyTotalHours', copyTotalHours)
        if (!sub) {
            totalHoursRef.current = copyTotalHours
            totalTotalHours.current = total
        }
        //console.log('totalTotalHours', total.totalActualHoursTotal, total.totalBudgetHoursTotal, total.totalPMEACHoursTotal)
        // setActualHoursRef ? setActualHoursRef(total.totalActualHoursTotal) : null
        // setBudgetHoursRef ? setBudgetHoursRef(total.totalBudgetHoursTotal) : null
        // setUpdatedBudgetHoursRef ? setUpdatedBudgetHoursRef(total.totalBudgetHoursTotal) : null
        // setEacHoursRef ? setEacHoursRef(total.totalPMEACHoursTotal) : null

        // const phases = sections && sections[index] ? sections[index] : []

        useEffect(() => {
            if (!overview && !isChild) {
                setActualHoursValue(total.totalActualHoursTotal)
                setBudgetHoursValue(total.totalBudgetHoursTotal)
                setUpdatedBudgetHoursValue(total.totalBudgetHoursTotal)
                setEacHoursValue(total.totalPMEACHoursTotal)
            }
        }, [total.totalActualHoursTotal, total.totalBudgetHoursTotal, total.totalPMEACHoursTotal])

        useEffect(() => {
            const rowData = {
                'Discipline Header': discipline,
                'Description': '',
                'Phase Code': '',
                'UOM': uom,
                'Original Budget QTY': dayZeroQuantity,
                'Revised Budget QTY': pNum(dayZeroQuantity) + pNum(coQuantity),
                'Installed QTY': totalInstalledQuantity,
                'Installed % Comp': percentComplete,
                'Budget Hrs/Qty': budgetHoursQty,
                'Actual Hrs/Qty': actualHoursQty,
                'PM Forecasted Hrs/Qty': forcastedRateHours,
                'Actual PF': pfHours,
                'PM Forecasted PF': pmPF,
                'Actual Hours': phaseActualHours,
                'Forecasted Hours': forecastedHours,
                'Earned Hours': earnedHours,
                'Budget Hours': budgetHours,
                'Trending EAC Hours': trendingEACHours,
                'PM EAC Hours': pmEACHours,
            }

            if (!sub) onSendToParent(index, rowData);
        }, [props])

        const statusOptions = [
            { label: 'Max', value: 'Max' },
            { label: 'Sum', value: 'Sum' },
            { label: 'Row', value: 'Row' },
            { label: 'Qty', value: 'Qty' }
        ]

        const saveMath = (value, phase) => {
            let data = {
                userId: '154045',
                userName: 'Seth Bailey',
                fieldName: 'math',
                tab: "Productivity Tracker",
                rowId: phase,
                version: selectedVersion + '',
                value: value + '',
                id: selectedJob,
                codeCode: phase,
                lockedVersion: lockedVersion
            }
            updateUserDataArray(data)
        }

        const saveMathRow = (value, phase) => {
            let data = {
                userId: '154045',
                userName: 'Seth Bailey',
                fieldName: 'mathRow',
                tab: "Productivity Tracker",
                rowId: phase,
                version: selectedVersion + '',
                value: value + '',
                id: selectedJob,
                codeCode: phase,
                lockedVersion: lockedVersion
            }
            updateUserDataArray(data)
        }

        if (overview) {
            const pfBg = pNum(pfHours) >= 1 ? '#dcfce7' : '#fee2e2';
            const pmPFBg = pNum(pmPF) >= 1 ? '#dcfce7' : '#fee2e2';
            const rowBg = sub ? '#ffffff' : '#f1f5f9';
            return (
                <tr style={{ fontWeight: sub ? 500 : 600, backgroundColor: rowBg, transition: 'background .15s', cursor: props.onToggle ? 'pointer' : 'default' }}
                    onClick={props.onToggle}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor='#e2e8f0'} onMouseLeave={e => e.currentTarget.style.backgroundColor=rowBg}>
                    <td style={{ ...ovStyles.tdDesc, fontWeight: sub ? 500 : 600, paddingLeft: sub ? 22 : 8 }}>{props.onToggle ? (props.open ? '▼ ' : '▶ ') : ''}{discipline}</td>
                    <td style={{ ...ovStyles.td, textAlign: 'center' }}>{uom}</td>
                    <td style={ovStyles.td}>{fNum(pNum(dayZeroQuantity) + pNum(coQuantity), 1)}</td>
                    <td style={ovStyles.td}>{totalInstalledQuantity > 0 ? fNum(pNum(totalInstalledQuantity), qpacUnit ? null : 1) : ''}</td>
                    <td style={ovStyles.td}>{percentComplete ? fNum(percentComplete, 1) + '%' : ''}</td>
                    <td style={ovStyles.td}>{budgetHoursQty}</td>
                    <td style={ovStyles.td}>{actualHoursQty}</td>
                    <td style={ovStyles.td}>{forcastedRateHours}</td>
                    <td style={{ ...ovStyles.td, background: pfBg, borderRadius: 0 }}>{pfHours}</td>
                    <td style={{ ...ovStyles.td, background: pmPFBg, borderRadius: 0 }}>{pmPF}</td>
                    <td style={ovStyles.td}>{fNum(pNum(phaseActualHours), 1)}</td>
                    <td style={ovStyles.td}>{fNum(pNum(earnedHours), 1)}</td>
                    <td style={ovStyles.td}>{fNum(pNum(budgetHours), 1)}</td>
                    <td style={ovStyles.td}>{fNum(pNum(trendingEACHours), 1)}</td>
                    <td style={ovStyles.td}>{fNum(pNum(pmEACHours), 1)}</td>
                </tr>
            );
        }

        return (
            <View style={{ flexDirection: 'row', backgroundColor: '#efefef' }}>
                {expanded && !overview ? <View style={{ flexDirection: 'row' }}>
                    <select
                        style={{
                            width: 75,
                            height: 24,
                            border: `1px solid ${isFocus ? 'blue' : '#ccc'}`,
                            padding: 3,
                            borderRadius: 3,
                            fontSize: 10
                        }}
                        value={status}
                        onFocus={() => setIsFocus(true)}
                        onBlur={() => setIsFocus(false)}
                        onChange={e => {
                            const value = e.target.value;
                            setStatus(value);
                            setIsFocus(false);
                            saveMath(value, discipline)
                            const mathCopy = JSON.parse(JSON.stringify(math))
                            mathCopy[discipline] = value
                            setMath(mathCopy)
                        }}
                    >
                        {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <TextInput style={s(40).cell}
                        value={mRow}
                        onChangeText={t => {
                            saveMathRow(t, discipline)
                            setMRow(t)
                            const mathRowCopy = JSON.parse(JSON.stringify(mathRow))
                            mathRowCopy[discipline] = t
                            setMathRow(mathRowCopy)
                            //setTriggerLoad(!triggerLoad)
                        }}
                    ></TextInput>
                </View> : null}
                <Text style={s(ovDesc).cell}>{discipline}</Text>
                {photo || overview ? null : <Text style={s(100).cell}></Text>}
                <Text style={s(50).cell}>{uom}</Text>
                {overview ? null : <Text style={s(100).cell}>{fNum(pNum(dayZeroQuantity), 1)}</Text>}
                <Text style={s(ov).cell}>{fNum(pNum(dayZeroQuantity) + pNum(coQuantity), 1)}</Text>
                <Text style={s(ov).cell}>{totalInstalledQuantity > 0 ? fNum(pNum(totalInstalledQuantity), 1) : ''}</Text>
                <Text style={s(ov).cell}>{percentComplete ? fNum(percentComplete, 1) + '%' : ''}</Text>
                <Text style={s(ov).cell}>{budgetHoursQty}</Text>
                <Text style={s(ov).cell}>{actualHoursQty}</Text>
                <Text style={s(ov).cell}>{forcastedRateHours}</Text>
                <Text style={[s(ovPf).cell, { backgroundColor: pNum(pfHours) >= 1 ? '#c6efce' : '#ffc7ce' }]}>{pfHours}</Text>
                <Text style={[s(ovPf).cell, { backgroundColor: pNum(pmPF) >= 1 ? '#c6efce' : '#ffc7ce' }]}>{pmPF}</Text>
                {renderPfExplanation(discipline)}
                <Text style={s(ov).cell}>{fNum(pNum(phaseActualHours), 1)}</Text>
                {/* <Text style={s(ov).cell}>{forecastedHours ? fNum(pNum(forecastedHours), 1) : ''}</Text> */}
                <Text style={s(ov).cell}>{fNum(pNum(earnedHours), 1)}</Text>
                <Text style={s(ov).cell}>{fNum(pNum(budgetHours), 1)}</Text>
                <Text style={s(ov).cell}>{fNum(pNum(trendingEACHours), 1)}</Text>
                <Text style={[s(ov).cell, {borderRight: '1px solid #aeadaf'}]}>{fNum(pNum(pmEACHours), 1)}</Text>
            </View >
        )
    }

    const saveValue = (value, phase) => {
        let uData = JSON.parse(JSON.stringify(disciplinesRef.current))
        let data = {
            userId: '154045',
            userName: 'Seth Bailey',
            fieldName: 'discipline',
            tab: "Productivity Tracker",
            rowId: phase,
            version: selectedVersion + '',
            value: value + '',
            id: selectedJob,
            codeCode: phase,
            lockedVersion: lockedVersion
        }

        uData[phase] = value
        disciplinesRef.current = uData
        setDisciplines(uData)

        console.log('disciplines', value, phase, disciplinesRef.current)
        updateUserDataArray(data)
    }

    // Shown as typed; saved once typing stops. Blank goes back to JDE's description.
    const saveDescription = (value, phase) => {
        if (!selectedJob || !phase) return
        setDescriptions((d) => ({ ...d, [phase]: value }))
        clearTimeout(descriptionTimers.current[phase])
        descriptionTimers.current[phase] = setTimeout(() => {
            updateUserDataArray({
                userId: '154045',
                userName: 'Seth Bailey',
                fieldName: 'description',
                tab: 'Productivity Tracker',
                rowId: phase,
                version: selectedVersion + '',
                value: value + '',
                id: selectedJob,
                codeCode: phase,
                lockedVersion: lockedVersion
            })
        }, 800)
    }

    const saveDisciplineMode = (mode) => {
        if (mode === disciplineMode) return
        setDisciplineMode(mode)
        if (selectedJob) {
            updateUserDataArray({
                userId: '154045',
                userName: 'Seth Bailey',
                fieldName: 'disciplineMode',
                tab: 'Productivity Tracker',
                rowId: '1',
                version: selectedVersion + '',
                value: mode,
                id: selectedJob,
                lockedVersion: lockedVersion
            })
        }
    }

    const modePill = (active) => ({
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        marginRight: 6,
        backgroundColor: active ? '#1e293b' : '#efefef',
        cursor: 'pointer'
    })
    const modePillText = (active) => ({
        fontSize: 10,
        fontWeight: 600,
        color: active ? 'white' : '#475569'
    })

    const pfExplanationOptions = [
        { group: 'Performing Well (PF ≥ 1)', options: ['Efficient Crew', 'Prefab / Planning', 'Favorable Conditions'] },
        { group: 'Underperforming (PF < 1)', options: ['Rework', 'Bad Productivity', 'Weather', 'Scope / Design Change', 'Material Delay'] },
        { group: 'Other', options: ['Scope Reduction', 'Other'] },
    ]

    const savePfExplanation = (value, rowKey) => {
        if (!selectedJob || !rowKey) return
        const copy = JSON.parse(JSON.stringify(pfExplanations))
        copy[rowKey] = value
        setPfExplanations(copy)
        updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            fieldName: 'pfExplanation',
            tab: 'Productivity Tracker',
            rowId: rowKey,
            version: selectedVersion + '',
            value: value + '',
            id: selectedJob,
            codeCode: rowKey,
            lockedVersion: lockedVersion
        })
    }

    const renderPfExplanation = (rowKey) => photo ? (
        <Text style={[s(ovExpl).cell, { textAlign: 'left' }]}>{pfExplanations[rowKey] || '—'}</Text>
    ) : (
        <select
            title={pfExplanations[rowKey] ? 'PF explanation: ' + pfExplanations[rowKey] : 'Select a reason for the PF'}
            value={pfExplanations[rowKey] || ''}
            onChange={e => savePfExplanation(e.target.value, rowKey)}
            style={{
                width: Math.round(ovExpl * 0.75),
                height: 24,
                border: '1px solid #aeadaf',
                padding: 3,
                fontSize: 10,
                backgroundColor: 'white'
            }}
        >
            <option value="">—</option>
            {pfExplanationOptions.map(g => (
                <optgroup key={g.group} label={g.group}>
                    {g.options.map(o => (
                        <option key={o} value={o}>{o}</option>
                    ))}
                </optgroup>
            ))}
        </select>
    )

    const updateState = () => {
        let uData = JSON.parse(JSON.stringify(disciplinesRef.current))
        setDisciplines(uData)
    }

    useEffect(() => {
        disciplinesRef.current = disciplines
        const standard = disciplineMode === 'standard'
        // Label with the area only when the job actually spans more than one area
        const areasPresent = new Set()
        if (standard) {
            phaseDescriptions.forEach((phase) => {
                const std = standardDisciplineFor(phase.code)
                if (std) areasPresent.add(std.area)
            })
        }
        const multiArea = areasPresent.size > 1
        const disciplineSections = {}
        const sectionOrder = {}
        phaseDescriptions.forEach((phase) => {
            let label
            let order = 999999
            if (standard) {
                const std = standardDisciplineFor(phase.code)
                label = std ? (multiArea ? 'Area ' + std.area + ' - ' + std.discipline : std.discipline) : 'Other'
                order = std ? std.area * 10000 + std.order : 999999
            } else {
                label = disciplines[phase.code] ? disciplines[phase.code] : 'Other'
            }
            phase.discipline = label
            if (disciplineSections[label]) {
                disciplineSections[label].push(phase)
                if (order < sectionOrder[label]) sectionOrder[label] = order
            } else {
                disciplineSections[label] = [phase]
                sectionOrder[label] = order
            }
        })
        //console.log('disciplineSections', disciplineSections, disciplines)
        const keys = Object.keys(disciplineSections)
        if (standard) keys.sort((a, b) => (sectionOrder[a] - sectionOrder[b]) || a.localeCompare(b))
        else if (overview) {
            // On the Overview the sections follow the budget table above them:
            // VoltaGrid in the bid's section order (Indirects, Piping, Coating,
            // Equipment, Electrical …) with a discipline the job named itself
            // right after the section its codes roll up to, otherwise
            // alphabetical; Other last.
            const vgOrder = (d) => voltaGridDisciplineRank(d, disciplineSections[d].map((p) => p.code))
            keys.sort((a, b) => (a === 'Other') - (b === 'Other') || (ovVoltaGrid ? vgOrder(a) - vgOrder(b) : 0) || a.localeCompare(b))
        }
        setSections(keys.map(k => disciplineSections[k]))
    }, [disciplines, phaseDescriptions, disciplineMode, overview, ovVoltaGrid])

    const collapse = () => {
        setExpanded(!expanded)
    }

    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                func(...args);
            }, delay);
        };
    }

    const debounceSave = useCallback(
        debounce(() => {
            console.log('here in save', changeListRef.current);
            saveObject(changeListRef.current);
            changeListRef.current = [];
        }, 2000),
        []
    );

        const saveObject = (listItems) => {
            console.log('saving', listItems);
            if (Array.isArray(listItems)) {
                const lastByKey = {};
                listItems.forEach(item => {
                    lastByKey[item.key] = item;
                });
                Object.values(lastByKey).forEach(({ key, value, buId, lockedVersion, version }) => {
                    if (value !== undefined) {
                        let data = {
                            userId: '154045',
                            userName: 'Seth Bailey',
                            fieldName: key,
                            tab: "Productivity Tracker",
                            rowId: 1,
                            version: version,
                            value: value + '',
                            id: buId,
                            lockedVersion: lockedVersion
                        };
                        updateUserDataArray(data);
                    }
                });
            }
        }


    const handleInputChange = (value, key) => {
        console.log('input change', key, value);
        changeListRef.current.push({ key: key, value: value, buId: selectedJob, lockedVersion: lockedVersion, version: selectedVersion });
        debounceSave();
    };

    // Standard-mode reference card: distinct disciplines this job maps to, per Area 1-4
    const areaDisciplines = { 1: [], 2: [], 3: [], 4: [] }
    if (disciplineMode === 'standard') {
        const seenArea = {}
        phaseDescriptions.forEach((phase) => {
            const std = standardDisciplineFor(phase.code)
            if (std && !seenArea[std.area + '|' + std.discipline]) {
                seenArea[std.area + '|' + std.discipline] = true
                areaDisciplines[std.area].push({ name: std.discipline, order: std.order })
            }
        })
        STANDARD_AREAS.forEach(a => areaDisciplines[a].sort((x, y) => x.order - y.order))
    }
    const areaRowCount = Math.max(1, ...STANDARD_AREAS.map(a => areaDisciplines[a].length))

    // Overview VoltaGrid: the phases of an Equipment / Electrical section by the
    // bid's WBS group (the QPAC installs, Cable Tray, Conduit …), in the bid's
    // order, matching the children in the budget table. Null for every other
    // section, and off the VoltaGrid tab.
    const [openVgGroups, setOpenVgGroups] = useState({})
    // A discipline the job named itself folds into the bid section its codes
    // roll up to, as in the budget table (AG Electrical Conduit and UG
    // Electrical into Electrical, Seth 10/22): one row under the section at its
    // first WBS group, keeping its own quantity. `into` is keyed by the
    // section, lower case; `folded` names the disciplines that moved.
    const vgFold = (() => {
        const into = {}
        const folded = {}
        if (!ovVoltaGrid) return { into, folded }
        const mapping = {}
        sections.forEach((phases) => phases.forEach((p) => { mapping[p.code] = p.discipline }))
        const { mapping: parentOf, order } = foldVoltaGridDisciplines(mapping)
        const present = new Set(sections.map((phases) => String(phases[0]?.discipline).toLowerCase()))
        sections.forEach((phases, i) => {
            const d = phases[0]?.discipline
            const parent = String(parentOf[phases[0]?.code]).toLowerCase()
            if (parent === String(d).toLowerCase() || !present.has(parent)) return
            folded[d] = true
            ;(into[parent] || (into[parent] = [])).push({ group: d, order: order[d], phases, index: i })
        })
        return { into, folded }
    })()
    const vgGroupsOf = (phases) => {
        if (!ovVoltaGrid || !phases?.length) return null
        if (!/^(equipment|electrical)$/i.test(String(phases[0]?.discipline || ''))) return null
        const groups = {}
        phases.forEach((p) => {
            const { group: g, order } = voltaGridGroupFor(p.code, phases[0]?.discipline)
            ;(groups[g] || (groups[g] = { group: g, order, phases: [] })).phases.push(p)
        })
        const foldedIn = vgFold.into[String(phases[0]?.discipline).toLowerCase()] || []
        return [...Object.values(groups), ...foldedIn].sort((a, b) => a.order - b.order || a.group.localeCompare(b.group))
            .map((g) => ({ label: g.group.replace(/^installation\s+qpac\s+/i, 'QPAC '), phases: g.phases, index: g.index }))
            .filter((g) => !vgGroupEmpty(g.phases))
    }
    // A group with nothing in it (no budget hours or quantity, no actual or
    // forecast hours) stays off the overview: Conduit – UG on 54026071 once
    // its code moved, Seth 10/30.
    const vgGroupEmpty = (ps) => ps.every((p) => {
        const actual = (Array.isArray(phases[p.code]) ? phases[p.code] : []).reduce((t, row) => t
            + (row.Cost_Type_Code && row.Cost_Type_Code[0] == '5' && row.Cost_Type_Code[1] == '1' && row.Cost_Type_Code[2] != '8' ? pNum(row.Total_Hours) || 0 : 0), 0)
        const forecast = regular == 'regular' ? pNum(userData[p.code]?.forecastedHrs) : pNum(etcHours[p.code])
        return !(pNum(p.dayZeroHours) || 0) && !(pNum(p.coHours) || 0) && !(pNum(p.dayZeroQuantity) || 0) && !(pNum(p.coQuantity) || 0)
            && !(pNum(userData[p.code]?.plug) || 0) && !actual && !(forecast || 0)
    })

    const ov = overview ? 75 : 100; // column width for data cells in overview vs normal
    const ovPf = overview ? 60 : 100; // narrower PF columns in overview
    const ovDesc = overview ? 200 : 260; // narrower discipline description in overview
    const ovExpl = overview ? 160 : 180; // PF explanation dropdown column

    if (overview) {
        return (
            <div style={{ width: '100%' }}>
                <table style={ovStyles.table}>
                    <thead>
                        <tr>
                            <th style={ovStyles.thGroup} rowSpan={2}>Discipline</th>
                            <th style={ovStyles.thGroup} colSpan={4}>Quantity</th>
                            <th style={ovStyles.thGroup} colSpan={3}>Hours / Quantity</th>
                            <th style={ovStyles.thGroup} colSpan={2}>PF</th>
                            <th style={ovStyles.thGroup} colSpan={5}>Hours Analysis</th>
                        </tr>
                        <tr>
                            <th style={ovStyles.th}>UoM</th>
                            <th style={ovStyles.th}>Revised Budget</th>
                            <th style={ovStyles.th}>Installed</th>
                            <th style={ovStyles.th}>% Comp</th>
                            <th style={ovStyles.th}>Budget</th>
                            <th style={ovStyles.th}>Actual</th>
                            <th style={ovStyles.th}>PM Forecast</th>
                            <th style={ovStyles.th}>Actual</th>
                            <th style={ovStyles.th}>PM Forecast</th>
                            <th style={ovStyles.th}>Actual</th>
                            <th style={ovStyles.th}>Earned</th>
                            <th style={ovStyles.th}>Budget</th>
                            <th style={ovStyles.th}>Trending EAC</th>
                            <th style={ovStyles.th}>PM EAC</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sections.map((phases, i) => {
                            const d = phases[0]?.discipline
                            if (vgFold.folded[d]) return null
                            const kids = vgGroupsOf(phases)
                            const open = !!openVgGroups[d]
                            const foldedPhases = (vgFold.into[String(d).toLowerCase()] || []).flatMap((g) => g.phases)
                            return (
                                <React.Fragment key={i}>
                                    <DisciplineRow phase={phases[0]} index={i} onSendData={handlePhaseRowData} foldedPhases={foldedPhases}
                                        onToggle={kids ? () => setOpenVgGroups((o) => ({ ...o, [d]: !o[d] })) : undefined} open={open} />
                                    {kids && open ? kids.map((g) => (g.index != null
                                        ? <DisciplineRow key={i + '|' + g.label} phase={g.phases[0]} index={g.index} nested />
                                        : <DisciplineRow key={i + '|' + g.label} phase={phases[0]} index={i} childLabel={g.label} childPhases={g.phases} />
                                    )) : null}
                                </React.Fragment>
                            )
                        })}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td style={ovStyles.tfoot}>Total</td>
                            <td style={ovStyles.tfoot}></td>
                            <td style={ovStyles.tfoot}></td>
                            <td style={ovStyles.tfoot}></td>
                            <td style={ovStyles.tfoot}></td>
                            <td style={ovStyles.tfoot}></td>
                            <td style={ovStyles.tfoot}></td>
                            <td style={ovStyles.tfoot}></td>
                            <td style={ovStyles.tfoot}>{totalActualPf(totalTotalHours.current)}</td>
                            <td style={ovStyles.tfoot}>{fNum(totalTotalHours.current.totalBudgetHoursTotal / totalTotalHours.current.totalPMEACHoursTotal)}</td>
                            <td style={ovStyles.tfoot}>{fNum(totalTotalHours.current.totalActualHoursTotal, 1)}</td>
                            <td style={ovStyles.tfoot}>{fNum(totalTotalHours.current.totalEarnedHoursTotal, 1)}</td>
                            <td style={ovStyles.tfoot}>{fNum(totalTotalHours.current.totalBudgetHoursTotal, 1)}</td>
                            <td style={ovStyles.tfoot}>{fNum(totalTotalHours.current.totalEACHoursTotal, 1)}</td>
                            <td style={ovStyles.tfoot}>{fNum(totalTotalHours.current.totalPMEACHoursTotal, 1)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    }

    return (
        <View style={{ padding: overview ? 0 : 8 }}>
            {/* The grid and the discipline cards sit side by side on a wide screen
                and wrap onto their own rows on a narrower one */}
            <View style={{ flexDirection: 'row', flexWrap: overview || photo ? 'nowrap' : 'wrap' }}>
                {/* On its own page the card is as wide as the grid and the page scrolls
                    sideways (nothing is clipped inside the card); the sticky header rows
                    stick to the page's scroll area. Embedded (overview) and PSR (photo)
                    views are laid out as before. */}
                <View ref={productivityRef} style={{ backgroundColor: 'white', borderRadius: 5, padding: overview ? 8 : 15, marginBottom: overview ? 0 : 15, marginRight: overview ? 0 : 75,
                    ...(overview || photo ? {} : { width: 'max-content' }) }}>
                  {/* the grid keeps its own width inside the scrolling card: columns scroll, not squeeze */}
                  <View style={{ minWidth: 'max-content' }}>
                    <View style={{ flexDirection: 'row', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                        {overview ? <Text style={s(ovDesc).title}></Text> : expanded ? <Text style={s(500).title}></Text> : <Text style={s(photo ? 310 : 360).title}></Text>}
                        {overview ? <Text style={s(ov * 3 + 50).title}>Quantity</Text> : photo ? <Text style={s(400).title}>Quantity</Text> : <Text style={s(450).title}>Quantity</Text>}
                        <Text style={s(overview ? ov * 3 : 300).title}>Hours/Quantity</Text>
                        <Text style={s(ovPf * 2 + ovExpl).title}>PF</Text>
                        <Text style={[s(overview ? ov * 5 : 500).title, {borderRight: '1px solid #aeadaf'}]}>Hours Analysis</Text>
                    </View>
                    <View style={{ flexDirection: 'row', position: 'sticky', top: 21, backgroundColor: 'white', zIndex: 1 }}>
                        {overview ? null : expanded ? <Text style={s(100).title}>Math</Text> : null}
                        {overview ? null : expanded ? <Text style={s(40).title}>Row</Text> : null}
                        <Text style={s(ovDesc).title}>Discipline - Task Description</Text>
                        {photo || overview ? null : <Text style={s(100).title}>Cost Code</Text>}
                        <Text style={s(50).title}>UoM</Text>
                        {overview ? null : <Text style={s(100).title}>Original Budget QTY</Text>}
                        <Text style={s(ov).title}>Revised Budget QTY</Text>
                        <Text style={s(ov).title}>Installed QTY</Text>
                        <Text style={s(ov).title}>Installed % Comp</Text>
                        <Text style={s(ov).title}>Budget Hrs/Qty</Text>
                        <Text style={s(ov).title}>Actual Hrs/Qty</Text>
                        <Text style={s(ov).title}>PM Forecasted Hrs/Qty</Text>
                        <Text style={s(ovPf).title}>Actual PF</Text>
                        <Text style={s(ovPf).title}>PM Forecasted PF</Text>
                        <Text style={s(ovExpl).title}>PF Explanation</Text>
                        <Text style={s(ov).title}>Actual Hours</Text>
                        {/* <Text style={s(100).title}>Forecasted Hours</Text> */}
                        <Text style={s(ov).title}>Earned Hours</Text>
                        <Text style={s(ov).title}>Budget Hours</Text>
                        <Text style={s(ov).title}>Trending EAC Hours</Text>
                        <Text style={[s(ov).title, {borderRight: '1px solid #aeadaf'}]}>PM EAC Hours</Text>
                    </View>
                    {sections.map((phases, i) => (
                        <View key={i}>
                            <DisciplineRow phase={phases[0]} index={i} onSendData={handlePhaseRowData} />
                            {expanded ? phases.map((phase, j) => (
                                <View key={j}>
                                    <PhaseRow phase={phase} index={j} identifier={i + '-' + j} onSendData={handlePhaseRowData} />
                                </View>
                            )) : null}
                        </View>
                    ))}
                    <View style={{ flexDirection: 'row' }}>
                        {overview ? <Text style={s(ovDesc).title}></Text> : expanded ? <Text style={s(500).title}></Text> : <Text style={s(photo ? 310 : 360).title}></Text>}
                        {overview ? <Text style={s(ov * 3 + 50).title}></Text> : photo ? <Text style={s(400).title}></Text> : <Text style={s(450).title}></Text>}
                        <Text style={s(overview ? ov * 3 : 300).title}></Text>
                        <Text style={s(ovPf).cell}>{totalActualPf(totalTotalHours.current)}</Text>
                        <Text style={s(ovPf).cell}>{fNum(totalTotalHours.current.totalBudgetHoursTotal / totalTotalHours.current.totalPMEACHoursTotal)}</Text>
                        <Text style={s(ovExpl).cell}></Text>
                        <Text style={s(ov).cell}>{fNum(totalTotalHours.current.totalActualHoursTotal, 1)}</Text>
                        {/* <Text style={s(ov).cell}>{totalTotalHours.current.totalForecastedHoursTotal ? fNum(totalTotalHours.current.totalForecastedHoursTotal, 1) : ''}</Text> */}
                        <Text style={s(ov).cell}>{fNum(totalTotalHours.current.totalEarnedHoursTotal, 1)}</Text>
                        <Text style={s(ov).cell}>{fNum(totalTotalHours.current.totalBudgetHoursTotal, 1)}</Text>
                        <Text style={s(ov).cell}>{fNum(totalTotalHours.current.totalEACHoursTotal, 1)}</Text>
                        <Text style={[s(ov).cell, {borderRight: '1px solid #aeadaf'}]}>{fNum(totalTotalHours.current.totalPMEACHoursTotal, 1)}</Text>
                    </View>
                    {overview ? null : <View style={{ marginTop: 8, backgroundColor: 'white', borderRadius: 5, padding: 15, height: 150 }}>
                        {photo ? (
                            <Text numberOfLines={8} style={{ flex: 1, padding: 15, fontSize: 12, backgroundColor: '#F9F8FB', borderRadius: 5, textAlignVertical: 'top', width: 1125 }}>
                                {userNotes || 'No notes'}
                            </Text>
                        ) : (
                            <TextInput
                                value={userNotes}
                                onChangeText={(n) => {
                                    setUserNotes(n)
                                    handleInputChange(n, 'notes')
                                }}
                                placeholder="Notes"
                                multiline={true}
                                style={{ flex: 1, padding: 15, fontSize: 16, backgroundColor: '#F9F8FB', borderRadius: 5 }}>
                            </TextInput>
                        )}
                    </View>}
                  </View>
                </View>
                {sections.map((phases, i) => (
                    <View key={i}>
                        {photo && !expanded ? phases.map((phase, j) => (
                            <View key={j}>
                                <PhaseRow phase={phase} index={j} identifier={i + '-' + j} onSendData={handlePhaseRowData} userData={userData} etcHours={etcHours} />
                            </View>
                        )) : null}
                    </View>
                ))}
                {photo || overview ? null : <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 15, marginTop: 15, marginBottom: 75, marginRight: 75 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 15 }}>
                        <Text style={{ fontSize: 11, fontWeight: 600, marginRight: 12 }}>Discipline Mapping</Text>
                        <Pressable style={modePill(disciplineMode !== 'standard')} onPress={() => saveDisciplineMode('user')}>
                            <Text style={modePillText(disciplineMode !== 'standard')}>User Defined</Text>
                        </Pressable>
                        <Pressable style={modePill(disciplineMode === 'standard')} onPress={() => saveDisciplineMode('standard')}>
                            <Text style={modePillText(disciplineMode === 'standard')}>Standard</Text>
                        </Pressable>
                    </View>
                    <View style={{ flexDirection: 'row', marginTop: 12 }}>
                        <Text style={s(100).title}>Phase</Text>
                        <Text style={s(300).title}>Description</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>Discipline</Text>
                    </View>
                    {phaseDescriptions.map((phase, i) => (
                        <View key={i} style={{ flexDirection: 'row' }}>
                            <Text style={s(100).title}>{phase.code}</Text>
                            {isVoltaGridJob(selectedJob) ? (
                                <TextInput style={[s(300).cell, { textAlign: 'left' }]}
                                    value={descriptions[phase.code] ?? ''}
                                    placeholder={phase.jdeDescription || ''}
                                    title={phase.jdeDescription ? 'JDE description: ' + phase.jdeDescription : ''}
                                    onChangeText={t => saveDescription(t, phase.code)}
                                ></TextInput>
                            ) : (
                                <Text style={s(300).title}>{phase.description}</Text>
                            )}
                            {disciplineMode === 'standard' ? (
                                <Text style={[s(300).cell, { borderRight: '1px solid #aeadaf', backgroundColor: '#f4f4f4', textAlign: 'left' }]}>
                                    {phase.discipline || 'Other'}
                                </Text>
                            ) : (
                            <View>
                                <View style={{ width: 2, height: 21, backgroundColor: '#efefef', left: 4, top: 3, position: 'absolute', zIndex: 2 }}></View>
                                <TextInput style={[s(300).cell, {borderRight: '1px solid #aeadaf'}]}
                                    value={disciplines[phase.code]}
                                    ref={e => { inputsRef.current[phase.code + '-discipline'] = e }}
                                    onFocus={() => { focusedId.current = phase.code + '-discipline' }}  // Track the input when focused
                                    onChangeText={t => {
                                        // clearTimeout(textTimeout)
                                        saveValue(t, phase.code)
                                        setLocalFocusedId(phase.code + '-discipline')
                                        // setTextTimeout(setTimeout(() => {
                                        //     updateState()
                                        // }, 1000))
                                    }}
                                ></TextInput>
                            </View>
                            )}
                        </View>
                    ))}
                </View>}
                {photo || overview ? null : disciplineMode === 'standard' ? <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 15, marginTop: 15, marginBottom: 75, marginRight: 75, overflowX: 'auto', maxWidth: '100%' }}>
                    <Text style={{ fontSize: 11, fontWeight: 600, marginTop: 15, marginBottom: 12 }}>Standard WBS Disciplines by Area</Text>
                    <View style={{ flexDirection: 'row', backgroundColor: '#efefef' }}>
                        {STANDARD_AREAS.map((a, i) => (
                            <Text key={a} style={[s(300).title, i === STANDARD_AREAS.length - 1 ? { borderRight: '1px solid #aeadaf' } : null]}>Area {a}</Text>
                        ))}
                    </View>
                    {Array.from({ length: areaRowCount }).map((_, r) => (
                        <View key={r} style={{ flexDirection: 'row' }}>
                            {STANDARD_AREAS.map((a, i) => (
                                <Text key={a} style={[s(300).title, i === STANDARD_AREAS.length - 1 ? { borderRight: '1px solid #aeadaf' } : null]}>
                                    {areaDisciplines[a][r] ? areaDisciplines[a][r].name : ''}
                                </Text>
                            ))}
                        </View>
                    ))}
                </View> : <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 15, marginTop: 15, marginBottom: 75, marginRight: 75 }}>
                    <View style={{ flexDirection: 'row', backgroundColor: '#efefef', marginTop: 50 }}>
                        <Text style={s(300).title}>Pipeline</Text>
                        <Text style={s(300).title}>Facilities</Text>
                        <Text style={s(300).title}>E&I</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>Engineering</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Project Support</Text>
                        <Text style={s(300).title}>Project Support</Text>
                        <Text style={s(300).title}>Project Support</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>Project Support</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Mob / Demob</Text>
                        <Text style={s(300).title}>Mob / Demob</Text>
                        <Text style={s(300).title}>Mob / Demob</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>Process</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Site Prep</Text>
                        <Text style={s(300).title}>Sitework</Text>
                        <Text style={s(300).title}>Cable Tray</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>Mechanical</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Pipe</Text>
                        <Text style={s(300).title}>Civil / Concrete</Text>
                        <Text style={s(300).title}>Conduit</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>Piping</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Paint / Coating</Text>
                        <Text style={s(300).title}>Structural Steel</Text>
                        <Text style={s(300).title}>Wire & Cable</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>Electrical</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Dirtwork</Text>
                        <Text style={s(300).title}>Equipment</Text>
                        <Text style={s(300).title}>Terminations</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>Instrumentation</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Tie-ins</Text>
                        <Text style={s(300).title}>Pipe</Text>
                        <Text style={s(300).title}>Electrical Equipment</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>Civil</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>HDD / Bore</Text>
                        <Text style={s(300).title}>Specialty Pipe</Text>
                        <Text style={s(300).title}>Instrumentation</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>Structural</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Testing</Text>
                        <Text style={s(300).title}>Demo</Text>
                        <Text style={s(300).title}>Civil/Concrete</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>Pipeline</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Concrete</Text>
                        <Text style={s(300).title}>Paint / Coat</Text>
                        <Text style={s(300).title}>T&M</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}>T&M</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Steel</Text>
                        <Text style={s(300).title}>Electrical and Instrumentation</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}></Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Electrical and Instrumentation</Text>
                        <Text style={s(300).title}>Building</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}></Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Building</Text>
                        <Text style={s(300).title}>Testing</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}></Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Pre-commissioning</Text>
                        <Text style={s(300).title}>Pre-Commissioning</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}></Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>Composite</Text>
                        <Text style={s(300).title}>T&M</Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}></Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={s(300).title}>T&M</Text>
                        <Text style={s(300).title}></Text>
                        <Text style={[s(300).title, {borderRight: '1px solid #aeadaf'}]}></Text>
                    </View>
                </View>}
            </View>
        </View>
    );
}));

export default ProductivityCombined;